import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserPlus, Mail, Lock, User, AlertCircle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { validatePassword, getStrengthColor, getStrengthBarColor, getStrengthBarWidth } from '../../lib/validation/password';
import { logger } from '../../lib/logger';

export function SignUpForm() {
  const { signUp } = useAuth();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setError(passwordValidation.message);
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, fullName);
      setSuccess(true);
      // Note: User will need to confirm email before they can log in
    } catch (err: any) {
      logger.error('[SignUpForm] Sign up error', err);
      if (err.message?.includes('already registered')) {
        setError('Este correo ya está registrado. Intenta iniciar sesión.');
      } else {
        setError(err.message || 'Error al crear la cuenta. Intenta de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl text-center"
      >
        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={40} className="text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#111827] mb-3">
          ¡Cuenta creada!
        </h2>
        <p className="text-[#6B7280] mb-6">
          Hemos enviado un correo de confirmación a <strong>{email}</strong>.
          Por favor revisa tu bandeja de entrada y confirma tu cuenta.
        </p>
        <Link
          to="/"
          className="inline-block px-6 py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] transition-colors shadow-lg shadow-[#5D1828]/30"
        >
          Ir a iniciar sesión
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-[#5D1828] to-[#7A2235] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <UserPlus size={32} className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-[#111827] mb-2">
          Crear cuenta
        </h1>
        <p className="text-[#6B7280]">
          Regístrate para comenzar
        </p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 text-sm"
        >
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Nombre completo
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="Juan Pérez"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Correo electrónico
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="tu@email.com"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="••••••••"
              required
              disabled={loading}
              minLength={8}
            />
          </div>
          {/* Password strength indicator */}
          {password.length > 0 && (
            <div className="mt-2">
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${getStrengthBarColor(validatePassword(password).strength)} ${getStrengthBarWidth(validatePassword(password).strength)}`}
                />
              </div>
              <p className={`text-xs mt-1 ${getStrengthColor(validatePassword(password).strength)}`}>
                {validatePassword(password).message}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-[#374151] mb-2">
            Confirmar contraseña
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={20} />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[#5D1828] focus:border-transparent transition-all"
              placeholder="••••••••"
              required
              disabled={loading}
              minLength={8}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-[#5D1828]/30 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Creando cuenta...
            </>
          ) : (
            <>
              <UserPlus size={20} />
              Crear cuenta
            </>
          )}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-[#6B7280]">
        ¿Ya tienes cuenta?{' '}
        <Link
          to="/"
          className="text-[#5D1828] font-semibold hover:text-[#7A2235] transition-colors"
        >
          Inicia sesión aquí
        </Link>
      </div>
    </motion.div>
  );
}
